import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    Box,
    CircularProgress,
    Typography,
} from "@mui/material";
import { PurchasePaymentCreate } from "../../services/procurementService";
import { formatCurrencyBDT } from "../../utils/currency";
import { AccountPicker } from "../accounts/AccountPicker";
import { ConfirmUntaggedPaymentDialog } from "../accounts/ConfirmUntaggedPaymentDialog";
import {
    PURCHASE_PAYMENT_METHODS,
    PAYMENT_METHOD_LABELS,
} from "../../utils/paymentMethods";

interface AddPurchasePaymentDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (data: PurchasePaymentCreate) => Promise<void>;
    amountDue: number;
}

const todayDateInput = () => new Date().toISOString().split("T")[0];

const defaultForm = (): PurchasePaymentCreate & { payment_date: string } => ({
    amount: 0,
    payment_method: "CASH",
    payment_date: todayDateInput(),
    reference_number: "",
    notes: "",
    account_id: null,
});

export const AddPurchasePaymentDialog: React.FC<
    AddPurchasePaymentDialogProps
> = ({ open, onClose, onConfirm, amountDue }) => {
    const [form, setForm] = useState(defaultForm);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [confirmUntaggedOpen, setConfirmUntaggedOpen] = useState(false);

    const handleOpen = () => {
        setForm({ ...defaultForm(), amount: amountDue > 0 ? amountDue : 0 });
        setError(null);
        setConfirmUntaggedOpen(false);
    };

    const handleClose = () => {
        if (saving) return;
        setForm(defaultForm());
        setError(null);
        onClose();
    };

    const submitPayment = async () => {
        setSaving(true);
        setError(null);
        try {
            await onConfirm({
                amount: form.amount,
                payment_method: form.payment_method,
                payment_date: form.payment_date
                    ? `${form.payment_date}T00:00:00`
                    : undefined,
                reference_number: form.reference_number || undefined,
                notes: form.notes || undefined,
                account_id: form.account_id || null,
            });
            setForm(defaultForm());
            onClose();
        } catch (err: any) {
            setError(
                err?.response?.data?.detail ||
                    err?.message ||
                    "Failed to record payment. Please try again.",
            );
        } finally {
            setSaving(false);
        }
    };

    const handleConfirm = () => {
        if (form.amount <= 0) {
            setError("Amount must be greater than 0.");
            return;
        }
        if (!form.account_id) {
            setConfirmUntaggedOpen(true);
            return;
        }
        submitPayment();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            TransitionProps={{ onEnter: handleOpen }}
        >
            <DialogTitle>Record Payment</DialogTitle>
            <DialogContent>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        mt: 1,
                    }}
                >
                    <TextField
                        label="Amount"
                        type="number"
                        value={form.amount}
                        onChange={(e) =>
                            setForm({
                                ...form,
                                amount: Number(e.target.value),
                            })
                        }
                        fullWidth
                        required
                        inputProps={{ min: 0, step: 0.01 }}
                        helperText={`Amount due: ${formatCurrencyBDT(amountDue)}`}
                        error={!!error && form.amount <= 0}
                    />
                    <TextField
                        select
                        label="Payment Method"
                        value={form.payment_method}
                        onChange={(e) =>
                            setForm({
                                ...form,
                                payment_method: e.target.value,
                            })
                        }
                        fullWidth
                        required
                    >
                        {PURCHASE_PAYMENT_METHODS.map((method) => (
                            <MenuItem key={method} value={method}>
                                {PAYMENT_METHOD_LABELS[method]}
                            </MenuItem>
                        ))}
                    </TextField>
                    <AccountPicker
                        value={form.account_id}
                        onChange={(account_id) => setForm({ ...form, account_id })}
                        helperText="Which cash/bank account this payment came out of"
                    />
                    <TextField
                        label="Payment Date"
                        type="date"
                        value={form.payment_date}
                        onChange={(e) =>
                            setForm({
                                ...form,
                                payment_date: e.target.value,
                            })
                        }
                        fullWidth
                        required
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label="Reference Number (Optional)"
                        value={form.reference_number}
                        onChange={(e) =>
                            setForm({
                                ...form,
                                reference_number: e.target.value,
                            })
                        }
                        fullWidth
                        placeholder="Transaction ID, check number, etc."
                    />
                    <TextField
                        label="Notes (Optional)"
                        value={form.notes}
                        onChange={(e) =>
                            setForm({ ...form, notes: e.target.value })
                        }
                        fullWidth
                        multiline
                        rows={2}
                        placeholder="Additional payment details..."
                    />
                    {error && (
                        <Typography variant="body2" color="error">
                            {error}
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={saving || form.amount <= 0}
                    startIcon={
                        saving ? <CircularProgress size={16} /> : undefined
                    }
                >
                    {saving ? "Saving..." : "Record Payment"}
                </Button>
            </DialogActions>

            <ConfirmUntaggedPaymentDialog
                open={confirmUntaggedOpen}
                amount={form.amount}
                confirmLabel="Record Payment"
                onCancel={() => setConfirmUntaggedOpen(false)}
                onConfirm={() => {
                    setConfirmUntaggedOpen(false);
                    submitPayment();
                }}
            />
        </Dialog>
    );
};

export default AddPurchasePaymentDialog;
